import React, { memo } from "react";
import { Center } from "@/components/atoms/Center";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Switch } from "@/components/atoms/Switch";
import { Text } from "@/components/atoms/Text";

import {
  InvitationsOrganizationUserFragment,
  OrganizationUserStatus,
} from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

/**
 * Props for the UserItemView component.
 */
interface Props {
  item: InvitationsOrganizationUserFragment;
  nameDisplay: string;
  isCurrentUser: boolean;
  loading: boolean;
  onToggle: () => void;
}

/**
 * View component for displaying a single organization user with status toggle control.
 * @param root0 - The component props
 * @param root0.item - The organization user data fragment containing role, title, and status
 * @param root0.nameDisplay - The formatted display name of the user
 * @param root0.isCurrentUser - Boolean indicating if this user is the currently logged-in user
 * @param root0.loading - Boolean indicating if activate/deactivate operation is in progress
 * @param root0.onToggle - Callback function to handle toggling user active/inactive status
 */
const UserItemView = ({
  item,
  nameDisplay,
  isCurrentUser,
  loading,
  onToggle,
}: Props) => (
  <Stack
    direction="horizontal"
    className={`items-center gap-3 ${ifWeb("py-3 px-2", "")}`}
  >
    <Stack className="flex-1">
      <Text
        variant="unstyled"
        className={`font-heading ${ifWeb("text-title-lg", "text-display-sm")}`}
      >
        {nameDisplay}
      </Text>
      <Text
        variant="unstyled"
        className={`font-medium ${ifWeb("text-title-sm", "text-title")} text-content-muted`}
      >
        {item.role?.name} - {item.user?.title}
      </Text>
    </Stack>
    {!isCurrentUser && (
      <Stack direction="horizontal" space="3" className="items-center">
        <Switch
          size="sm"
          defaultValue={item.status === OrganizationUserStatus.Active}
          isDisabled={loading}
          onValueChange={onToggle}
        />
        <Center className="w-14">
          {loading ? (
            <Spinner color="white" />
          ) : (
            <Text variant="body">
              {item.status === OrganizationUserStatus.Active
                ? "Active"
                : "Inactive"}
            </Text>
          )}
        </Center>
      </Stack>
    )}
  </Stack>
);

UserItemView.displayName = "UserItemView";

export default memo(UserItemView);
